import { gql, useMutation, useQuery } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogTitle,
  Button,
  ButtonGroup,
} from '@apollo/orbit';
import React, { useCallback, useRef, useState } from 'react';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import {
  PQDeleteModalMutation,
  PQDeleteModalMutationVariables,
  PQDeleteModalQuery,
  PQDeleteModalQueryVariables,
} from 'src/lib/graphqlTypes/types';

import { persistedQueryListQuery } from '../../queries';

interface Props {
  graphId: string;
  listId: string;
  persistedQueryIdToRemove: string;
  persistedQueryClientNameToRemove: string | null;
  isOpen: boolean;
  onClose: () => void;
  __test?: {
    mutationError?: Error;
    loading?: Boolean;
    submitting?: Boolean;
  };
}

const pqDeleteModalQuery = gql<PQDeleteModalQuery, PQDeleteModalQueryVariables>`
  query PQDeleteModalQuery($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        name
        description
        currentBuild {
          id
          totalOperationsInList
        }
        linkedVariants {
          id
          name
        }
      }
    }
  }
`;

const pqDeleteModalMutation = gql<
  PQDeleteModalMutation,
  PQDeleteModalMutationVariables
>`
  mutation PQDeleteModalMutation(
    $graphId: ID!
    $listId: ID!
    $idToRemove: ID!
    $clientNameToRemove: String
  ) {
    service(id: $graphId) {
      persistedQueryList(id: $listId) {
        id
        publishOperations(
          remove: [{ id: $idToRemove, clientName: $clientNameToRemove }]
        ) {
          ... on PublishOperationsResult {
            __typename
          }
          ... on CannotModifyOperationBodyError {
            message
            __typename
          }
          ... on PermissionError {
            message
            __typename
          }
        }
      }
    }
  }
`;

export const PersistedQueryDeleteModal = ({
  graphId,
  listId,
  persistedQueryIdToRemove,
  persistedQueryClientNameToRemove,
  isOpen,
  onClose,
  __test,
}: Props) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const {
    data: pqListData,
    loading: pqListLoading,
    error: pqListError,
  } = useQuery(pqDeleteModalQuery, {
    variables: { graphId, listId },
  });

  const pqList = pqListData?.graph?.persistedQueryList;

  const [mutate, { loading: mutationLoading, error: mutationError }] =
    useMutation(pqDeleteModalMutation, {
      variables: {
        graphId,
        listId,
        idToRemove: persistedQueryIdToRemove,
        clientNameToRemove: persistedQueryClientNameToRemove,
      },
      refetchQueries: [persistedQueryListQuery],
      awaitRefetchQueries: true,
    });
  const [failureMessage, setFailureMessage] = useState<string | undefined>();

  const handelSubmit = useCallback(async () => {
    setFailureMessage(undefined);
    const { data } = await mutate();
    if (!data?.service?.persistedQueryList) {
      // we handle errors reactively above
      return;
    }
    switch (data.service.persistedQueryList.publishOperations.__typename) {
      case 'PublishOperationsResult':
        onClose();
        break;
      default:
        setFailureMessage(
          data.service.persistedQueryList.publishOperations.message,
        );
        break;
    }
  }, [mutate, onClose]);

  const loading = pqListLoading || Boolean(__test?.loading);
  const submitting = mutationLoading || Boolean(__test?.submitting);
  const hasLinkedVariants = (pqList?.linkedVariants.length ?? 0) > 0;

  if (loading) {
    return (
      <AlertDialog
        isOpen={isOpen}
        size="xl"
        onClose={onClose}
        leastDestructiveRef={cancelButtonRef}
      >
        <AlertDialogOverlay />
        <FormWrapper onSubmit={onClose}>
          <AlertDialogContent>
            <AlertDialogBody>
              <Loading size="md" />
            </AlertDialogBody>
          </AlertDialogContent>
        </FormWrapper>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      size="xl"
      onClose={onClose}
      leastDestructiveRef={cancelButtonRef}
    >
      <AlertDialogOverlay />
      <FormWrapper onSubmit={handelSubmit}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to delete this operation?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            {hasLinkedVariants ? (
              <>
                <p className="text-neutral">
                  Deleting operations will remove them from your persisted query
                  list, which will affect the following linked variants:
                  <ul className="ml-6 list-disc">
                    {pqList &&
                      pqList.linkedVariants.map((v) => (
                        <li key={v.id}>{v.name}</li>
                      ))}
                  </ul>
                </p>
                <p className="mt-4 text-neutral">
                  This is a destructive action that cannot be undone. Take
                  special care when removing operations, especially if your
                  router is using persisted queries or enforcing safelisting. If
                  this operation is in use by a client, removing it may result
                  in the operation being blocked.
                </p>
              </>
            ) : (
              <>
                <p className="text-neutral">
                  Deleting operations will remove them from this persisted query
                  list. This is a destructive action that cannot be undone.
                </p>
              </>
            )}

            {(pqListError || mutationError || __test?.mutationError) && (
              <ErrorMessage className="mt-2">
                Something went wrong. Please refresh and try again.
              </ErrorMessage>
            )}
            {failureMessage && (
              <ErrorMessage className="mt-2">{failureMessage}</ErrorMessage>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                ref={cancelButtonRef}
                variant="secondary"
                type="button"
                isDisabled={submitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                isDisabled={loading}
                isLoading={submitting}
                loadingText="Yes, delete"
                type="submit"
              >
                Yes, delete
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </FormWrapper>
    </AlertDialog>
  );
};
